import { Box, IconButton, TablePagination, type TablePaginationProps, type TablePaginationActionsProps } from '@mui/material';
import {
  FirstPage,
  KeyboardArrowLeft,
  KeyboardArrowRight,
  LastPage,
} from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import type { MouseEvent } from 'react';

interface LinkPaginationProps extends Omit<TablePaginationProps, 'ActionsComponent'> {
  getPageLink: (page: number) => string;
}

function LinkButton({
  disabled,
  targetPage,
  getPageLink,
  ariaLabel,
  children,
  onPageChange,
}: {
  disabled: boolean;
  targetPage: number;
  getPageLink: (page: number) => string;
  ariaLabel?: string;
  children: React.ReactNode;
  onPageChange?: TablePaginationProps['onPageChange'];
}) {
  const handleClick = (event: MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => {
    if (disabled) {
      event.preventDefault();
      return;
    }

    onPageChange?.(event as MouseEvent<HTMLButtonElement>, targetPage);
  };

  if (disabled) {
    return (
      <IconButton disabled aria-label={ariaLabel} size="small">
        {children}
      </IconButton>
    );
  }

  return (
    <IconButton
      component={RouterLink}
      to={getPageLink(targetPage)}
      aria-label={ariaLabel}
      size="small"
      onClick={handleClick}
    >
      {children}
    </IconButton>
  );
}

type WrappedPaginationActionsProps = TablePaginationActionsProps & {
  getPageLink: (page: number) => string;
};

function LinkPaginationActions({
  count = 0,
  page,
  rowsPerPage,
  onPageChange,
  showFirstButton,
  showLastButton,
  getPageLink,
  getItemAriaLabel,
}: WrappedPaginationActionsProps) {
  const safeCount = Number.isFinite(count) ? count : 0;
  const lastPage = Math.max(0, Math.ceil(safeCount / rowsPerPage) - 1);

  return (
    <Box sx={{ flexShrink: 0, ml: 2.5, display: 'flex', gap: 0.5 }}>
      {showFirstButton && (
        <LinkButton
          disabled={page === 0}
          targetPage={0}
          getPageLink={getPageLink}
          ariaLabel={getItemAriaLabel?.('first')}
          onPageChange={onPageChange}
        >
          <FirstPage />
        </LinkButton>
      )}
      <LinkButton
        disabled={page === 0}
        targetPage={page - 1}
        getPageLink={getPageLink}
        ariaLabel={getItemAriaLabel?.('previous')}
        onPageChange={onPageChange}
      >
        <KeyboardArrowLeft />
      </LinkButton>
      <LinkButton
        disabled={page >= lastPage}
        targetPage={page + 1}
        getPageLink={getPageLink}
        ariaLabel={getItemAriaLabel?.('next')}
        onPageChange={onPageChange}
      >
        <KeyboardArrowRight />
      </LinkButton>
      {showLastButton && (
        <LinkButton
          disabled={page >= lastPage}
          targetPage={lastPage}
          getPageLink={getPageLink}
          ariaLabel={getItemAriaLabel?.('last')}
          onPageChange={onPageChange}
        >
          <LastPage />
        </LinkButton>
      )}
    </Box>
  );
}

export default function LinkPagination({
  getPageLink,
  showFirstButton = true,
  showLastButton = true,
  ...props
}: LinkPaginationProps) {
  return (
    <TablePagination
      {...props}
      showFirstButton={showFirstButton}
      showLastButton={showLastButton}
      ActionsComponent={(actionsProps) => (
        <LinkPaginationActions {...actionsProps} getPageLink={getPageLink} />
      )}
    />
  );
}
